import { useCallback, useEffect, useState } from 'react'
import {
  getShopPlan,
  grantShopPlan,
  revokeShopPlan,
  type ShopPlanGrantPayload,
  type ShopPlanRead,
  type ShopPlanSource,
  type ShopPlanTier,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface PlanPanelProps {
  shopId: string
}

const TIER_OPTIONS: { value: ShopPlanTier; label: string }[] = [
  { value: 'paid_creator', label: 'Paid Creator' },
  { value: 'free_creator', label: 'Free Creator' },
]

const SOURCE_OPTIONS: { value: ShopPlanSource; label: string }[] = [
  { value: 'manual_grant', label: 'Manual grant' },
  { value: 'comp', label: 'Comp' },
  { value: 'early_shops_program', label: 'Early Shops Program' },
  { value: 'paid', label: 'Paid (post-signup)' },
]

function statusBadge(status: ShopPlanRead['status']) {
  const map = {
    active: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    expired: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  }
  return map[status]
}

export function PlanPanel({ shopId }: PlanPanelProps) {
  const [plan, setPlan] = useState<ShopPlanRead | null>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [tier, setTier] = useState<ShopPlanTier>('paid_creator')
  const [source, setSource] = useState<ShopPlanSource>('manual_grant')
  const [durationDays, setDurationDays] = useState('365')
  const [indefinite, setIndefinite] = useState(false)

  const canEdit = usePermission('shops', 'edit')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getShopPlan(shopId)
      if (res.ok) setPlan(res.data ?? null)
    } finally {
      setLoading(false)
    }
  }, [shopId])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setTier('paid_creator')
    setSource('manual_grant')
    setDurationDays('365')
    setIndefinite(false)
    setFormError(null)
  }

  const handleGrant = async () => {
    setFormError(null)
    let duration: number | null
    if (indefinite) {
      duration = null
    } else {
      const parsed = Number.parseInt(durationDays, 10)
      if (Number.isNaN(parsed) || parsed < 1 || parsed > 3650) {
        setFormError('Duration must be a whole number of days between 1 and 3650.')
        return
      }
      duration = parsed
    }
    const payload: ShopPlanGrantPayload = {
      tier,
      source,
      duration_days: duration,
    }
    setSubmitting(true)
    const res = await grantShopPlan(shopId, payload)
    setSubmitting(false)
    if (!res.ok) {
      setFormError(res.error?.message || 'Failed to grant plan.')
      return
    }
    resetForm()
    setShowForm(false)
    await load()
  }

  const handleRevoke = async () => {
    if (!plan) return
    setSubmitting(true)
    setFormError(null)
    const res = await revokeShopPlan(shopId)
    setSubmitting(false)
    if (!res.ok) {
      setFormError(res.error?.message || 'Failed to revoke plan.')
      return
    }
    await load()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Active plan</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Personal shops have no plan. A Creator shop has exactly one active plan.
          </p>
        </div>
        {canEdit && !showForm && (
          <CustomButton onClick={() => setShowForm(true)} variant="primary">
            Grant new plan
          </CustomButton>
        )}
      </div>

      {plan ? (
        <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#2a1f35] p-4">
          <div className="mb-2 flex items-center gap-2">
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge(plan.status)}`}
            >
              {plan.status}
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
              {plan.tier.replace('_', ' ')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-gray-500 dark:text-gray-400">Source</div>
              <div className="font-mono text-xs">{plan.source}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Started</div>
              <div className="text-xs">{new Date(plan.started_at).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Expires</div>
              <div className="text-xs">
                {plan.expires_at ? new Date(plan.expires_at).toLocaleString() : 'Indefinite'}
              </div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Last event</div>
              <div className="text-xs">{new Date(plan.last_event_at).toLocaleString()}</div>
            </div>
          </div>
          {canEdit && plan.status === 'active' && (
            <div className="mt-4">
              <CustomButton onClick={handleRevoke} disabled={submitting} variant="danger">
                {submitting ? 'Revoking...' : 'Revoke plan'}
              </CustomButton>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4 text-sm text-gray-600 dark:text-gray-300">
          No active plan. This shop is either Personal or has never been granted a Creator
          tier.
        </div>
      )}

      {showForm && (
        <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#2a1f35] p-4">
          <h5 className="mb-3 font-medium text-gray-900 dark:text-gray-100">Grant plan</h5>
          <p className="mb-4 text-xs text-gray-600 dark:text-gray-300">
            Granting a new plan cancels any currently-active plan. Early Shops Program
            grants use 365 days.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tier</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value as ShopPlanTier)}
                className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2.5 text-sm"
              >
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Source</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as ShopPlanSource)}
                className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2.5 text-sm"
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={indefinite}
                  onChange={(e) => setIndefinite(e.target.checked)}
                />
                Indefinite (never expires)
              </label>
            </div>
            {!indefinite && (
              <div className="md:col-span-2">
                <CustomTextInput
                  label="Duration (days)"
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                  placeholder="365"
                  type="number"
                  min={1}
                  max={3650}
                />
              </div>
            )}
          </div>
          {formError && (
            <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
              {formError}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <CustomButton onClick={handleGrant} disabled={submitting} variant="primary">
              {submitting ? 'Granting...' : 'Grant plan'}
            </CustomButton>
            <CustomButton
              onClick={() => {
                resetForm()
                setShowForm(false)
              }}
              variant="secondary"
            >
              Cancel
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  )
}
